import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

export class HomeCarousel {
    autoPlayDuration: string;
    container: HTMLElement;
    swiper: Swiper;
    progressInterval: NodeJS.Timeout | null = null;
    controls: Element;
    playBtn: HTMLElement;
    pauseBtn: HTMLElement;
    isPlaying: boolean;

    constructor(container: HTMLElement, duration: string) {
        this.autoPlayDuration = duration;
        this.container = container;
        this.controls = this.container.querySelector('[data-carousel-controls]');
        this.playBtn = this.controls.querySelector('[data-carousel-play]') as HTMLElement;
        this.pauseBtn = this.controls.querySelector('[data-carousel-pause]') as HTMLElement;
        this.isPlaying = true;
        this.initSwiper();
        this.bindEvents();
        this.startBulletProgress();
    }

    initSwiper() {
        this.swiper = new Swiper(this.container, {
            modules: [Navigation, Pagination, Autoplay],
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                renderBullet: (index, className) => {
                    const slides = this.container.querySelectorAll('.swiper-slide');
                    const slide = slides[index];
                    const label = slide?.getAttribute('data-label');
                    return `
                    <button class="${className}" type="button" data-slide-index="${index}">
                        <div class="home-carousel-progress-bar" data-progress-bar></div>
                        <span class="swiper-pagination-bullet-title small">${label}</span>
                    </button>
                `;
                }
            },
            autoplay: {
                delay: parseFloat(this.autoPlayDuration),
                disableOnInteraction: false
            },
            loop: true,
            watchSlidesProgress: true,
            speed: 800,
            effect: 'slide',
            grabCursor: true,
            centeredSlides: false,
            slidesPerView: 1,
            spaceBetween: 0,
            longSwipesRatio: 0.1,
            longSwipesMs: 300,
            followFinger: true,
            threshold: 10,
            touchRatio: 1,
            touchAngle: 45,
            simulateTouch: true,
            preventInteractionOnTransition: false,
            allowTouchMove: true,
            freeMode: false,
            resistance: true,
            resistanceRatio: 0.85,

        });
    }

    bindEvents() {
        this.swiper.on('slideChangeTransitionStart', () => {
            setTimeout(() => {
                this.resetAllProgressBars();
            }, 150);
        });

        this.swiper.on('slideChangeTransitionEnd', () => this.startBulletProgress());

        this.swiper.on('autoplayStart', () => {
            this.isPlaying = true;
            this.updatePlayPauseButtons();
            this.startBulletProgress();
        });
        this.swiper.on('autoplayStop', () => {
            this.isPlaying = false;
            this.updatePlayPauseButtons();
        });
        this.swiper.on('autoplayResume', () => {
            this.isPlaying = true;
            this.updatePlayPauseButtons();
            this.startBulletProgress();
        });

        this.controls.addEventListener('click', () => this.togglePlayPause());
        this.bindPaginationClicks();
    }

    bindPaginationClicks() {
        const pagination = this.container.querySelector('.swiper-pagination');
        if (pagination) {
            pagination.addEventListener('click', (e) => {
                const bullet = (e.target as HTMLElement).closest('.swiper-pagination-bullet');
                if (!bullet) return;

                const slideIndex = parseInt(bullet.getAttribute('data-slide-index'));
                if (isNaN(slideIndex)) return;

                this.swiper.slideToLoop(slideIndex, 0);

                if (!this.isPlaying) {
                    this.swiper.autoplay.start();
                    this.isPlaying = true;
                    this.updatePlayPauseButtons();
                }

                this.startBulletProgress();
            });
        }
    }

    updatePlayPauseButtons() {
        if (this.isPlaying) {
            this.playBtn.classList.add('hide');
            this.pauseBtn.classList.remove('hide');
        } else {
            this.playBtn.classList.remove('hide');
            this.pauseBtn.classList.add('hide');
        }
    }

    resetAllProgressBars() {
        const allProgressBars = this.container.querySelectorAll('[data-progress-bar]');
        allProgressBars.forEach((bar) => {
            const progressBar = bar as HTMLElement;
            progressBar.style.transition = 'none';
            progressBar.style.width = '0%';
        });
    }

    startBulletProgress() {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
        }

        const activeIndex = this.swiper.realIndex;
        const activeBullet = this.container.querySelectorAll('.swiper-pagination-bullet')[activeIndex];
        const activeProgressBar = activeBullet?.querySelector('[data-progress-bar]') as HTMLElement;

        if (!activeProgressBar) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                activeProgressBar.style.transition = `width ${this.autoPlayDuration}ms linear`;
                activeProgressBar.style.width = '100%';
            });
        });
    }

    togglePlayPause() {
        if (this.isPlaying) {
            this.swiper.autoplay.stop();
            if (this.progressInterval) {
                clearInterval(this.progressInterval);
            }

            this.resetAllProgressBars();

            this.isPlaying = false;
        } else {
            this.swiper.autoplay.start();
            this.startBulletProgress();
            this.isPlaying = true;
        }

        this.updatePlayPauseButtons();
    }
}

const carouselElement = document.querySelector('[data-home-carousel]') as HTMLElement;

if (carouselElement) {
    const carouselDuration = carouselElement.getAttribute('data-carousel-duration');
    new HomeCarousel(carouselElement, carouselDuration);
}